// v2.7.65+E133 · Voucher Ledger — public rider-facing lookup page
// ═══════════════════════════════════════════════════════════════════
// Riders type their phone or email and see every voucher on their
// account: serial, allocated-to, status, days remaining, $ value.
// Nothing sensitive is exposed beyond the first name of the applicant.

import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import {
  Ticket, Search, Phone, Mail, CheckCircle2, XCircle, AlertTriangle,
  Clock, User, Users, Calendar, DollarSign, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/errors";

/* ─────────────────────────── helpers ──────────────────────────── */
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const statusStyles = {
  issued: { bg: "bg-emerald-500/15 border-emerald-400/50 text-emerald-300", icon: CheckCircle2, label: "ACTIVE" },
  used:   { bg: "bg-zinc-700/40 border-zinc-500/40 text-zinc-300",         icon: Ticket,        label: "USED" },
  voided: { bg: "bg-rose-500/15 border-rose-400/50 text-rose-300",         icon: XCircle,       label: "VOIDED" },
  expired:{ bg: "bg-amber-500/15 border-amber-400/50 text-amber-300",      icon: AlertTriangle, label: "EXPIRED" },
};

/* ─────────────────────────── page ─────────────────────────────── */
export default function VoucherLedgerPage() {
  const [phone,    setPhone]    = useState("");
  const [email,    setEmail]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);   // full ledger response
  const [searched, setSearched] = useState(false);

  const activeCount = useMemo(
    () => (result?.vouchers || []).filter((v) => v.status === "issued").length,
    [result]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim() && !email.trim()) {
      toast.error("Enter your phone number or email to look up your vouchers.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/vouchers/ledger", {
        phone: phone.trim() || null,
        email: email.trim() || null,
      });
      setResult(data);
      setSearched(true);
      if (!data.match_count) {
        toast.info("No vouchers found for that contact. Double-check the phone/email you used on your application.");
      }
    } catch (err) {
      toast.error(formatApiError(err, "Could not look up your vouchers."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout>
      <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 sm:px-6 lg:px-10 py-8" data-testid="voucher-ledger-page">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-500/10 via-rose-500/10 to-transparent p-6 sm:p-8 mb-6 shadow-xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="rounded-xl bg-amber-500 text-black p-3"><Ticket className="h-7 w-7"/></div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-amber-300 uppercase tracking-tight" data-testid="ledger-title">
                  My CCVAP Vouchers
                </h1>
                <p className="text-xs sm:text-sm text-amber-200 font-bold mt-0.5">
                  Community Care Voucher Assistance Program · Rider Portal
                </p>
                <p className="text-sm text-zinc-300 mt-1">
                  Look up how many Community Care vouchers you have left, when they expire, and which family members they cover.
                </p>
              </div>
            </div>
          </div>

          {/* Lookup form */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="ledger-lookup-card">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Search className="h-5 w-5 text-amber-400"/>Look Up My Vouchers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
                    <Phone className="h-3 w-3"/>Phone number
                  </label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(580) 555-0123"
                    className="bg-zinc-950 border-zinc-700 text-zinc-100"
                    data-testid="ledger-phone-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
                    <Mail className="h-3 w-3"/>OR email address
                  </label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    className="bg-zinc-950 border-zinc-700 text-zinc-100"
                    data-testid="ledger-email-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
                    data-testid="ledger-lookup-submit"
                  >
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Looking up…</> : <><Search className="h-4 w-4 mr-2"/>Show my vouchers</>}
                  </Button>
                </div>
              </form>
              <p className="mt-3 text-[11px] text-zinc-500">
                Use the same phone or email you provided on your CCVAP Program application.
              </p>
            </CardContent>
          </Card>

          {/* Summary */}
          {searched && result?.match_count > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="ledger-summary">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/40 p-3 text-center">
                <div className="text-2xl font-black text-emerald-300" data-testid="ledger-summary-issued">{result.summary.issued}</div>
                <div className="text-[10px] uppercase text-emerald-200/80 tracking-widest font-bold mt-1">Active</div>
              </div>
              <div className="rounded-lg bg-zinc-800 border border-zinc-700 p-3 text-center">
                <div className="text-2xl font-black text-zinc-300">{result.summary.used}</div>
                <div className="text-[10px] uppercase text-zinc-400 tracking-widest font-bold mt-1">Used</div>
              </div>
              <div className="rounded-lg bg-rose-500/10 border border-rose-400/40 p-3 text-center">
                <div className="text-2xl font-black text-rose-300">{result.summary.voided}</div>
                <div className="text-[10px] uppercase text-rose-200/80 tracking-widest font-bold mt-1">Voided</div>
              </div>
              <div className="rounded-lg bg-amber-500/10 border border-amber-400/40 p-3 text-center">
                <div className="text-2xl font-black text-amber-300">${(result.value_per_ride_usd || 0).toFixed(0)}</div>
                <div className="text-[10px] uppercase text-amber-200/80 tracking-widest font-bold mt-1">Per Ride</div>
              </div>
            </div>
          )}

          {/* Ledger list */}
          {searched && result?.vouchers?.length > 0 && (
            <div className="space-y-2" data-testid="ledger-list">
              <div className="text-sm text-zinc-400 mb-2">
                Hi {result.rider_first_name || "there"} — you have <span className="text-amber-300 font-bold">{activeCount}</span> active voucher{activeCount === 1 ? "" : "s"}.
              </div>
              {result.vouchers.map((v) => {
                const s = statusStyles[v.status] || statusStyles.issued;
                const Icon = s.icon;
                const urgent = v.status === "issued" && v.days_remaining !== null && v.days_remaining <= 14;
                return (
                  <div
                    key={v.voucher_serial_number}
                    className={`rounded-xl border-2 p-3 flex items-center gap-3 ${s.bg} ${urgent ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-zinc-950" : ""}`}
                    data-testid={`ledger-voucher-${v.voucher_serial_number}`}
                  >
                    <div className="rounded-lg bg-black/30 p-2 flex-shrink-0">
                      <Icon className="h-5 w-5"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-black">{v.voucher_serial_number}</span>
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-black bg-black/40">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-300 flex-wrap">
                        <span className="flex items-center gap-1">
                          {v.allocated_to_type === "child" ? <Users className="h-3 w-3"/> : <User className="h-3 w-3"/>}
                          {v.allocated_to_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3"/>{(v.value_usd || 0).toFixed(2)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3"/>Expires {formatDate(v.expires_at)}
                        </span>
                      </div>
                    </div>
                    {v.status === "issued" && v.days_remaining !== null && (
                      <div className="text-right flex-shrink-0" data-testid={`ledger-countdown-${v.voucher_serial_number}`}>
                        <div className={`text-2xl font-black ${urgent ? "text-amber-300" : "text-emerald-300"}`}>{v.days_remaining}</div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 justify-end">
                          <Clock className="h-3 w-3"/>days left
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {searched && result?.match_count === 0 && (
            <Card className="bg-zinc-900 border-zinc-800" data-testid="ledger-empty">
              <CardContent className="py-10 text-center">
                <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3"/>
                <div className="text-lg font-bold text-zinc-200 mb-1">No vouchers found</div>
                <div className="text-sm text-zinc-400 max-w-md mx-auto">
                  We couldn&apos;t find any vouchers matching that phone or email. Make sure you&apos;re using the same contact you provided on your <a className="text-amber-300 hover:underline" href="/voucher-program">CCVAP Program application</a>.
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MarketingLayout>
  );
}
